(function () {
  const API_KEY = 'a5881573d130184e53e5c6f2';
  const BASE_CURRENCY = 'AED';
  const GEO_API = 'https://ipwho.is/?fields=success,country_code';
  const GEO_CACHE_KEY = 'geo_detected_currency';
  const GEO_FETCH_TIMEOUT_MS = 3000;

  // IMPORTANT: verify each handle is correct. Switch to each market on
  // the storefront and check window.ShopifyMarketHandle in the console
  // (or temporarily use the debug version of this script) — if a market
  // shows a currency that doesn't match, its handle here is wrong.
  const MARKET_CURRENCY_OVERRIDES = {
    'uae': 'AED',
    'ksa': 'SAR',
    'oman': 'OMR',
    'kuwait': 'KWD',
    'bahrain': 'BHD',
    'qatar': 'QAR'
  };

  // Primary source for International-market currency detection.
  // ipwho.is stopped reliably returning a `currency` object, so we
  // derive currency from country_code instead — extend as needed.
  const COUNTRY_CODE_TO_CURRENCY = {
    EG: 'EGP', US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR',
    PT: 'EUR', IE: 'EUR', AT: 'EUR', GR: 'EUR', FI: 'EUR',
    AE: 'AED', SA: 'SAR', OM: 'OMR', KW: 'KWD', BH: 'BHD', QA: 'QAR',
    JO: 'JOD', LB: 'LBP', IQ: 'IQD', YE: 'YER',
    IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR',
    MA: 'MAD', TN: 'TND', DZ: 'DZD', LY: 'LYD', SD: 'SDG',
    TR: 'TRY', RU: 'RUB', UA: 'UAH', IL: 'ILS',
    CN: 'CNY', JP: 'JPY', KR: 'KRW', SG: 'SGD', HK: 'HKD',
    TH: 'THB', VN: 'VND', PH: 'PHP', MY: 'MYR', ID: 'IDR',
    CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN',
    CZ: 'CZK', HU: 'HUF', RO: 'RON',
    ZA: 'ZAR', NG: 'NGN', KE: 'KES', GH: 'GHS',
    BR: 'BRL', MX: 'MXN', AR: 'ARS', CL: 'CLP', CO: 'COP'
  };

  let rates = {};
  let cachedTargetCurrency = null; // resolved once, reused on every refresh

  // Start fetching immediately — don't wait for DOMContentLoaded
  const ratesReady = (async function () {
    const cached = sessionStorage.getItem('fx_rates');
    if (cached) {
      rates = JSON.parse(cached);
      return;
    }
    try {
      const res = await fetch(`https://v6.exchangerate-api.com/v6/${API_KEY}/latest/${BASE_CURRENCY}`);
      const data = await res.json();
      rates = data.conversion_rates;
      sessionStorage.setItem('fx_rates', JSON.stringify(rates));
    } catch (e) {
      console.warn('Currency fetch failed:', e);
    }
  })();

  function convertAllPrices(targetCurrency, root = document) {
    const rate = rates[targetCurrency];
    if (!rate) return;

    root.querySelectorAll('span.money').forEach(el => {
      if (!el.dataset.aedValue) {
        const raw = el.textContent.replace(/[^0-9.]/g, '');
        el.dataset.aedValue = parseFloat(raw);
      }

      const converted = Math.round(parseFloat(el.dataset.aedValue) * rate).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });

      el.textContent = `${converted} ${targetCurrency}`;
    });
  }

  // Only called when the shopper is in the International (catch-all)
  // market, since that market alone doesn't tell us which country
  // they're actually in.
  async function detectGeoCurrency() {
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached) return cached;

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), GEO_FETCH_TIMEOUT_MS);

      const res = await fetch(GEO_API, { signal: controller.signal });
      clearTimeout(timeout);

      const data = await res.json();

      if (data && data.success && data.country_code) {
        const code = COUNTRY_CODE_TO_CURRENCY[data.country_code];
        if (code) {
          sessionStorage.setItem(GEO_CACHE_KEY, code);
          return code;
        }
        console.warn(`No currency mapped for country_code "${data.country_code}". Add it to COUNTRY_CODE_TO_CURRENCY.`);
      }
    } catch (e) {
      console.warn('Geolocation currency detection failed:', e);
    }
    return null;
  }

  async function getTargetCurrency() {
    const marketHandle = window.ShopifyMarketHandle;
    const internationalHandle = window.ShopifyInternationalMarketHandle;

    // A specific configured market is a deliberate choice — use the
    // hardcoded override for it, since Shopify's own currency value
    // can't be trusted here.
    if (marketHandle && internationalHandle && marketHandle !== internationalHandle) {
      const overriddenCurrency = MARKET_CURRENCY_OVERRIDES[marketHandle];
      if (overriddenCurrency) return overriddenCurrency;

      console.warn(
        `No currency override configured for market handle "${marketHandle}". ` +
        `Add it to MARKET_CURRENCY_OVERRIDES. Falling back to ${BASE_CURRENCY}.`
      );
      return BASE_CURRENCY;
    }

    // International market: try to detect the shopper's real currency
    // by IP. Only use it if we actually have an exchange rate for it,
    // otherwise fall back to base.
    const geoCurrency = await detectGeoCurrency();
    if (geoCurrency && rates[geoCurrency]) {
      return geoCurrency;
    }
    return BASE_CURRENCY;
  }

  async function whenReady() {
    await ratesReady;
    if (!cachedTargetCurrency) {
      cachedTargetCurrency = await getTargetCurrency();
    }
    return cachedTargetCurrency;
  }

  async function refreshPrices(root) {
    const currency = await whenReady();
    if (currency && currency !== BASE_CURRENCY) {
      convertAllPrices(currency, root);
    }
  }

  // Rescales a price-range filter's slider + number inputs from AED into
  // the shopper's detected currency, so the whole slider (drag math,
  // typed values, reset-to-default checks) operates consistently in the
  // display currency from then on. Safe to call more than once — skips
  // elements already rescaled to the current currency.
  async function rescalePriceFilter(container) {
    if (!container) return;
    const targetCurrency = await whenReady();
    if (!targetCurrency || targetCurrency === BASE_CURRENCY) return;
    const rate = rates[targetCurrency];
    if (!rate) return;

    container.querySelectorAll('[data-range-slider]').forEach(slider => {
      if (slider.dataset.currencyRescaled === targetCurrency) return;
      ['data-se-min', 'data-se-max', 'data-se-min-value', 'data-se-max-value'].forEach(attr => {
        if (slider.hasAttribute(attr)) {
          const aedVal = parseFloat(slider.getAttribute(attr));
          if (!isNaN(aedVal)) slider.setAttribute(attr, Math.round(aedVal * rate));
        }
      });
      slider.dataset.currencyRescaled = targetCurrency;
    });

    container.querySelectorAll('[data-field-price-min], [data-field-price-max]').forEach(input => {
      if (input.dataset.currencyRescaled === targetCurrency) return;
      if (input.value) input.value = Math.round(parseFloat(input.value) * rate);
      if (input.placeholder) input.placeholder = Math.round(parseFloat(input.placeholder) * rate);
      input.dataset.currencyRescaled = targetCurrency;
    });

    container.querySelectorAll('.filter__price__field span').forEach(symbolSpan => {
      symbolSpan.textContent = targetCurrency;
    });
  }

  // Converts the "Price: 50 - 200 X" active-filter pill into the
  // shopper's currency. Uses the raw AED values stored in data
  // attributes rather than parsing the displayed text, since that
  // text is locale-formatted and fragile to parse back apart.
  async function rescaleActiveFilterPill(container) {
    if (!container) return;
    const targetCurrency = await whenReady();
    if (!targetCurrency || targetCurrency === BASE_CURRENCY) return;
    const rate = rates[targetCurrency];
    if (!rate) return;

    container.querySelectorAll('[data-price-filter-pill]').forEach(pill => {
      if (pill.dataset.currencyRescaled === targetCurrency) return;

      const minEl = pill.querySelector('[data-price-filter-min-display]');
      const maxEl = pill.querySelector('[data-price-filter-max-display]');
      const minAed = parseFloat(pill.getAttribute('data-price-filter-min'));
      const maxAed = parseFloat(pill.getAttribute('data-price-filter-max'));

      if (minEl && !isNaN(minAed)) {
        minEl.textContent = Math.round(minAed * rate).toLocaleString('en-US');
      }
      if (maxEl && !isNaN(maxAed)) {
        maxEl.textContent = Math.round(maxAed * rate).toLocaleString('en-US');
      }
      pill.dataset.currencyRescaled = targetCurrency;
    });
  }
    // Converts literal "AED 500" style text (used for gift card
  // denomination option values, which aren't run through Shopify's
  // money filter) into the shopper's currency. Scans actual rendered
  // text rather than requiring template changes, so it works
  // regardless of whether the theme renders options as radio buttons
  // or a dropdown.
  function walkTextNodes(root, callback) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    nodes.forEach(callback);
  }

  async function convertDenominationLabels(root) {
    if (!root) return;
    const targetCurrency = await whenReady();
    if (!targetCurrency || targetCurrency === BASE_CURRENCY) return;
    const rate = rates[targetCurrency];
    if (!rate) return;

    const pattern = new RegExp(`\\b${BASE_CURRENCY}\\s*([\\d,]+(?:\\.\\d+)?)\\b`, 'g');

    walkTextNodes(root, (node) => {
      const original = node.textContent;
      const updated = original.replace(pattern, (match, num) => {
        const aedVal = parseFloat(num.replace(/,/g, ''));
        const converted = Math.round(aedVal * rate).toLocaleString('en-US');
        return `${targetCurrency} ${converted}`;
      });
      if (updated !== original) node.textContent = updated;
    });
  }

  window.CurrencyConverter = {
    refreshPrices,
    getMarketHandle: () => window.ShopifyMarketHandle || null,
    getCurrentCurrency: () => cachedTargetCurrency,
    getRate: (currency) => rates[currency] || null,
    whenReady,
    rescalePriceFilter,
    rescaleActiveFilterPill,
    convertDenominationLabels
  };

  document.addEventListener('DOMContentLoaded', () => {
    refreshPrices();
    const variantOptions = document.querySelector('.form__selectors');
    if (variantOptions) {
      convertDenominationLabels(variantOptions);
    }
  });

  // Fires after every variant-change section morph (see changeVariant
  // in theme.js) — the morph brings back fresh, unconverted AED HTML,
  // so re-run both conversions afterward.
  window.addEventListener('theme:section:update', () => {
    refreshPrices();
    const variantOptions = document.querySelector('.form__selectors');
    if (variantOptions) {
      convertDenominationLabels(variantOptions);
    }
  });
})();